import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useMessageStore } from '../stores/messageStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import ConversationList from '../components/ConversationList';
import ChatThread from '../components/ChatThread';

// Realtime 연결이 끊겨도 동작하도록 안전장치 — 30초 fallback poll
const FALLBACK_POLL_INTERVAL = 30000;

export default function ChatPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const conversations = useMessageStore((s) => s.conversations);
  const currentConversation = useMessageStore((s) => s.currentConversation);
  const currentMessages = useMessageStore((s) => s.currentMessages);
  const loading = useMessageStore((s) => s.loading);
  const fetchConversations = useMessageStore((s) => s.fetchConversations);
  const fetchMessages = useMessageStore((s) => s.fetchMessages);
  const sendMessage = useMessageStore((s) => s.sendMessage);
  const markRead = useMessageStore((s) => s.markRead);
  const clearCurrent = useMessageStore((s) => s.clearCurrent);
  const subscribeToConversation = useMessageStore((s) => s.subscribeToConversation);
  const unsubscribeFromConversation = useMessageStore((s) => s.unsubscribeFromConversation);

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { replace: true });
  }, [isAuthenticated, navigate]);

  // 대화 목록 — 진입 시 한 번 로드.
  // inbox 채널은 Header에서 전역으로 구독되어 새 메시지 도착 시 자동 갱신됨.
  // fallback poll은 Realtime 끊김 대비 30초.
  useEffect(() => {
    if (!user) return;
    fetchConversations();
    const fallback = setInterval(fetchConversations, FALLBACK_POLL_INTERVAL);
    return () => clearInterval(fallback);
  }, [user?.id, fetchConversations]);

  // 선택된 대화 채널 구독
  useEffect(() => {
    if (!conversationId) {
      clearCurrent();
      return;
    }
    fetchMessages(conversationId);
    markRead(conversationId);
    subscribeToConversation(conversationId);
    return () => {
      unsubscribeFromConversation();
    };
  }, [conversationId, fetchMessages, markRead, clearCurrent, subscribeToConversation, unsubscribeFromConversation]);

  if (!isAuthenticated || !user) return null;

  const handleSend = async (body: string) => {
    if (!conversationId) return;
    await sendMessage(conversationId, body);
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full overflow-hidden">
        <AdminSidebar />

        <main className="flex-1 flex overflow-hidden">
          <div className={`w-80 bg-white border-r border-gray-200 flex flex-col ${conversationId ? 'hidden md:flex' : ''}`}>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                메시지
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {conversations.length > 0
                  ? `대화 ${conversations.length}개`
                  : '시작된 대화가 없습니다'}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ConversationList
                conversations={conversations}
                currentUserId={user.id}
                selectedId={conversationId || null}
                onSelect={(c) => navigate(`/chat/${c.id}`)}
                loading={loading && conversations.length === 0}
              />
            </div>
          </div>

          <div className={`flex-1 flex flex-col bg-white ${conversationId ? '' : 'hidden md:flex md:items-center md:justify-center'}`}>
            {currentConversation ? (
              <ChatThread
                conversation={currentConversation}
                messages={currentMessages}
                currentUserId={user.id}
                onSend={handleSend}
              />
            ) : (
              <div className="text-center text-gray-400 p-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-3" />
                <p className="text-sm">대화를 선택해주세요.</p>
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
